import { useState } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { EventItem } from '@/components/events/EventItem'
import {
  EventsFilterBar,
  type EventCountry,
  type EventImpactFilter,
  type EventSourceFilter,
} from '@/components/events/EventsFilterBar'
import { useEventsList, useRefreshEvents } from '@/lib/useEvents'
import { useToastStore } from '@/stores/toast'

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function addDaysIso(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

export function EventsPage() {
  const [impact, setImpact] = useState<EventImpactFilter>('all')
  const [country, setCountry] = useState<EventCountry>('all')
  const [source, setSource] = useState<EventSourceFilter>('all')
  const [fromDate, setFromDate] = useState(todayIso)
  const [toDate, setToDate] = useState(() => addDaysIso(30))

  const query = useEventsList({
    from: fromDate,
    to: toDate,
    impact: impact === 'all' ? undefined : impact,
    countries: country === 'all' ? undefined : [country],
  })
  const refresh = useRefreshEvents()
  const pushToast = useToastStore((s) => s.pushToast)

  const handleRefresh = () => {
    refresh.mutate(undefined, {
      onSuccess: (stats) => {
        const summary = `${stats.inserted} neu, ${stats.updated} aktualisiert`
        if (stats.error) {
          pushToast({
            kind: 'error',
            message: 'Refresh teilweise fehlgeschlagen',
            detail: `${summary} · Fehler: ${stats.error}`,
          })
        } else {
          pushToast({
            kind: 'success',
            message: 'Events aktualisiert',
            detail: summary,
          })
        }
      },
      onError: () => pushToast({ kind: 'error', message: 'Refresh fehlgeschlagen' }),
    })
  }

  const filteredEvents = (query.data?.items ?? []).filter(
    (e) => source === 'all' || e.event_type === source,
  )

  return (
    <AppShell>
      <article className="fade-in max-w-4xl">
        <header className="flex items-end justify-between gap-6">
          <div>
            <p className="tracked-caps text-[10px] text-faint">Kalender</p>
            <h2 className="mt-2 text-2xl text-ink">Events</h2>
            <p className="mt-1 font-mono text-xs text-muted">{filteredEvents.length} im Zeitraum</p>
          </div>
        </header>

        <div className="mt-8">
          <EventsFilterBar
            impact={impact}
            country={country}
            source={source}
            fromDate={fromDate}
            toDate={toDate}
            isRefreshing={refresh.isPending}
            onImpactChange={setImpact}
            onCountryChange={setCountry}
            onSourceChange={setSource}
            onFromChange={setFromDate}
            onToChange={setToDate}
            onRefresh={handleRefresh}
          />
        </div>

        <div className="mt-6">
          {query.isLoading ? (
            <p className="font-mono text-sm text-muted">Lade Events…</p>
          ) : query.isError ? (
            <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
              <p className="tracked-caps text-[10px] text-loss">Fehler</p>
              <p className="mt-2 font-mono text-sm text-ink">
                Events konnten nicht geladen werden.
              </p>
              <p className="mt-1 font-mono text-xs text-muted">{query.error?.message}</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="border border-hairline bg-panel px-6 py-12 text-center">
              <p className="tracked-caps text-[10px] text-faint">Leer</p>
              <p className="mt-3 text-sm text-muted">Keine Events im gewählten Zeitraum.</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {filteredEvents.map((event) => (
                <EventItem key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </article>
    </AppShell>
  )
}
